import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { Fragment, useId, useMemo } from 'react'

import Product from '@src/quince-site-common/src/domains/Product'
import ProductOptionValue, {
  ProductOptionDisplayType,
  ProductOptionName,
} from '@src/quince-site-common/src/domains/ProductOption'
import { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { areAllColorVariantsOnSale } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { partition } from '@src/utils/functions/utils'

import { useGetOutOfStockLastChanceSizes } from '../../hooks/useGetOutOfStockLastChanceSizes'

import { DISPLAY_TYPE_VS_OPTION_RENDERER } from './OptionSelector.constant'
import styles from './optionSelector.module.scss'

export interface OptionSelectorProps {
  className?: string
  optionDisplayTypesVsClassName?: Record<string, string | undefined>
  optionValueList: ProductOptionValue[]
  selectedValue: string | undefined
  onSelect: (value: string) => void
  isAllRoundOptions?: boolean
  showSelectedName?: boolean
  classNameOption?: string
  product?: Product
  selectedOptions?: ProductVariantOption[]
  optionWrapperClassName?: string
  selectorKey?: string
  isPDP?: boolean
  showMoreOption?: boolean
  optionLimit?: number
  onShowMoreClick?: () => void
  renderAfterColorComponent?: () => React.ReactNode
  displayName?: string
  excessOptionsTextClassName?: string
  productPurchaseInfo?: IProductPurchaseInfo
  inputOptionError?: string
  showNewPdpDesignV2?: boolean
}

const OptionSelector = ({
  isAllRoundOptions = true,
  showMoreOption,
  optionLimit,
  onShowMoreClick,
  renderAfterColorComponent,
  displayName,
  excessOptionsTextClassName,
  productPurchaseInfo,
  product,
  selectedOptions,
  showNewPdpDesignV2,
  ...props
}: OptionSelectorProps) => {
  const { t } = useTranslation('common')
  const { optionValueList } = props
  const selectionSetId = useId()
  const classes = classnames(props.className, styles.optionListWrapper, {
    [`${styles.optionListWrapper__withRowPadding}`]: !isAllRoundOptions && !showNewPdpDesignV2,
  })

  let salePrice = 0

  const validOptionValueList = useMemo(
    () =>
      //eslint-disable-next-line
      (optionValueList || [])
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        .filter(optionValue => !!DISPLAY_TYPE_VS_OPTION_RENDERER[optionValue.displayType])
        .filter(
          optionValue =>
            !!(optionValue.hexCode ?? (optionValue.displayType !== ProductOptionDisplayType.TEXT || props.isPDP)),
        ),
    [optionValueList],
  )

  const lastChanceSizes = useGetOutOfStockLastChanceSizes({
    product,
    displayName,
    productPurchaseInfo,
    selectedOptions,
  })
  const { filteredOptionValueList, noOfExcessElements } = useMemo(() => {
    const noOfExcessElements = Math.max(validOptionValueList.length - (optionLimit ?? 0), 0)

    if (!showMoreOption || !noOfExcessElements) {
      return { filteredOptionValueList: validOptionValueList, noOfExcessElements: 0 }
    }

    const [selectedOptionArray, unSelectedOptionArray] = partition(
      validOptionValueList,
      optionValue => optionValue.value === props.selectedValue,
    )

    const numberOfSelectedOptions = selectedOptionArray.length

    const visibleOptionsArray = [
      ...selectedOptionArray,
      ...unSelectedOptionArray.slice(0, (optionLimit ?? 0) - numberOfSelectedOptions),
    ]

    return { filteredOptionValueList: visibleOptionsArray, noOfExcessElements }
  }, [validOptionValueList, showMoreOption, optionLimit])

  return (
    <>
      <div className={classes} data-content-type="option-selector">
        {filteredOptionValueList.map((optionValue, index) => {
          const OptionRenderer = DISPLAY_TYPE_VS_OPTION_RENDERER[optionValue.displayType]
          if (optionValue.displayType === ProductOptionDisplayType.PACK && product) {
            const updatedSelectedOptions: ProductVariantOption[] =
              productHelper.getUpdatedSelectedOptionsOnProductOptionSelect(
                product,
                selectedOptions ?? [],
                ProductOptionName.Size,
                optionValue.value,
              )
            salePrice = productHelper.getQuincePriceForOptions(product, updatedSelectedOptions)
          }
          const inputOptionError =
            optionValue.displayType === ProductOptionDisplayType.INPUT ? props.inputOptionError : undefined

          const showRedDot = props.isPDP && areAllColorVariantsOnSale(product, optionValue)
          return (
            <Fragment key={optionValue.value}>
              <div
                data-content-type="option-wrapper"
                data-value={optionValue.value}
                data-is-selected={optionValue.value === props.selectedValue}
                data-selector-key={index === 0 ? props.selectorKey : undefined}
                className={classnames(
                  styles.optionWrapper,
                  {
                    [`${styles.optionWrapper__packOptionWrapper}`]:
                      optionValue.displayType === ProductOptionDisplayType.PACK,
                    [`${styles.optionWrapper__engravingOptionWrapper}`]:
                      optionValue.displayType === ProductOptionDisplayType.INPUT,
                  },
                  props.optionWrapperClassName,
                  showNewPdpDesignV2 && styles.optionWrapper_newDesignV2,
                )}
              >
                <OptionRenderer
                  className={classnames(
                    props.optionDisplayTypesVsClassName?.[optionValue.displayType],
                    props.classNameOption,
                  )}
                  selectionSetId={selectionSetId}
                  optionValue={optionValue}
                  isSelected={optionValue.value === props.selectedValue}
                  addborder={optionValue.addBorder}
                  isRound={isAllRoundOptions}
                  onChange={props.onSelect}
                  salePrice={salePrice}
                  maxCharacterLimit={optionValue.maxCharacterLimit}
                  minCharacterLimit={optionValue.minCharacterLimit}
                  lastChanceSizes={lastChanceSizes}
                  value={optionValue.value}
                  inputOptionError={inputOptionError}
                  showNewPdpDesignV2={showNewPdpDesignV2}
                  showRedDot={showRedDot}
                />
              </div>
              {props.showSelectedName && optionValue.value === props.selectedValue && (
                <span className={styles.optionName}>{optionValue.value}</span>
              )}
            </Fragment>
          )
        })}
        {noOfExcessElements > 0 ? (
          <div
            className={classnames(styles.excessElements, excessOptionsTextClassName)}
            onClick={onShowMoreClick}
            role={t('button')}
          >
            +{noOfExcessElements}
          </div>
        ) : null}
      </div>
      {renderAfterColorComponent?.()}
    </>
  )
}

export default OptionSelector
